/**
 * <BrandPilotsAdminPage> - Iter 9.40 Pass D / E
 *
 * Admin enrollment + listing for the Brand Pilot Program (MAC, Fenty, etc.).
 * Endpoints:
 *   GET  /api/brand-pilots
 *   POST /api/brand-pilots/enroll  { host_user_id, brand_id, tier? }
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrandPilotsAdminPage() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState({ host_user_id: "", brand_id: "", tier: "standard" });

  const fetchEnrollments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/brand-pilots`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setEnrollments(data.enrollments || []);
      }
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchEnrollments(); }, []);

  const enroll = async () => {
    if (!draft.host_user_id || !draft.brand_id) {
      toast.error("host_user_id and brand_id required");
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/brand-pilots/enroll`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (res.ok) {
        toast.success("Enrolled");
        setDraft({ host_user_id: "", brand_id: "", tier: "standard" });
        await fetchEnrollments();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to enroll");
      }
    } catch { toast.error("Failed"); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-6" data-testid="brand-pilots-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Brand Pilot Program</h2>
          <p className="text-sm text-gray-500">Enroll hosts as brand attribution partners (MAC, Fenty, etc.).</p>
        </div>
        <button onClick={fetchEnrollments} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="brand-pilots-refresh">
          <RefreshCw className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Enroll form */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="text-sm font-semibold mb-1">Enroll a host</div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Host user_id</Label>
              <Input
                value={draft.host_user_id}
                onChange={(e) => setDraft({ ...draft, host_user_id: e.target.value })}
                placeholder="usr_mia_makeup"
                className="mt-1"
                data-testid="brand-pilot-host"
              />
            </div>
            <div>
              <Label className="text-xs">Brand id</Label>
              <Input
                value={draft.brand_id}
                onChange={(e) => setDraft({ ...draft, brand_id: e.target.value })}
                placeholder="mac"
                className="mt-1"
                data-testid="brand-pilot-brand"
              />
            </div>
            <div>
              <Label className="text-xs">Tier</Label>
              <select
                value={draft.tier}
                onChange={(e) => setDraft({ ...draft, tier: e.target.value })}
                className="w-full mt-1 text-sm rounded-md border border-gray-300 px-2 py-1.5"
                data-testid="brand-pilot-tier"
              >
                <option value="standard">standard</option>
                <option value="elite">elite</option>
                <option value="legacy">legacy</option>
              </select>
            </div>
          </div>
          <Button size="sm" onClick={enroll} disabled={submitting} data-testid="brand-pilot-enroll">
            <Plus className="w-4 h-4 mr-1" /> {submitting ? "Enrolling…" : "Enroll"}
          </Button>
        </CardContent>
      </Card>

      {/* Enrollments table */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Active enrollments ({enrollments.length})</h3>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : enrollments.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500" data-testid="brand-pilots-empty">
            No enrollments yet.
          </div>
        ) : (
          <div className="space-y-2">
            {enrollments.map((e) => (
              <Card key={e.enrollment_id} data-testid={`brand-pilot-row-${e.enrollment_id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{e.host_user_id}</div>
                    <div className="text-xs text-gray-500">
                      <span className="font-mono">{e.attribution_code}</span>
                    </div>
                  </div>
                  <Badge variant="outline" className="capitalize">{e.brand_id}</Badge>
                  <Badge variant="secondary" className="capitalize">{e.tier}</Badge>
                  <Badge className="bg-green-50 text-green-700 border-green-200" variant="outline">{e.status}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
